import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, UserCog, Mail, ShieldCheck, Pencil, KeyRound, Lock } from "lucide-react";
import { api, formatApiError, STAFF_PERMISSIONS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_PERMS = ["analytics", "requests", "quoting", "fulfillment"];
const PERM_LABEL = Object.fromEntries(STAFF_PERMISSIONS.map((p) => [p.key, p.label]));

function PermissionPicker({ value, onChange, idPrefix }) {
  const toggle = (key, checked) =>
    onChange(checked ? [...new Set([...value, key])] : value.filter((k) => k !== key));
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="permission-picker">
      {STAFF_PERMISSIONS.map((p) => {
        const checked = value.includes(p.key);
        return (
          <label key={p.key}
            className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors ${checked ? "border-blue-500 bg-blue-50/60" : "border-slate-200 hover:bg-slate-50"}`}>
            <Checkbox checked={checked} onCheckedChange={(c) => toggle(p.key, !!c)} data-testid={`${idPrefix}-perm-${p.key}`} className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-slate-800">{p.label}</span>
              <span className="block text-xs text-slate-500">{p.desc}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function StaffDialog({ trigger, title, initial, onSubmit, requirePassword }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial); /* eslint-disable-next-line */ }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="sd-name">Nom</Label>
            <Input id="sd-name" data-testid="staff-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
          </div>
          {form.email !== undefined && (
            <div>
              <Label htmlFor="sd-email">Email (login)</Label>
              <Input id="sd-email" data-testid="staff-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
          )}
          <div>
            <Label htmlFor="sd-pass">{requirePassword ? "Mot de passe" : "Nouveau mot de passe (optionnel)"}</Label>
            <Input id="sd-pass" data-testid="staff-password-input" type="password" required={requirePassword}
              minLength={6} value={form.password || ""} placeholder={requirePassword ? "" : "Laisser vide pour ne pas changer"}
              onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label className="mb-2 block">Accès accordés</Label>
            <PermissionPicker value={form.permissions} onChange={(p) => setForm({ ...form, permissions: p })} idPrefix={requirePassword ? "new" : "edit"} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="save-staff-btn" className="bg-blue-600 hover:bg-blue-700">
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminStaff() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);

  const load = () => api.get("/admin/agents").then(({ data }) => setTeam(data)).catch(() => setTeam([]));
  useEffect(() => { load(); }, []);

  const create = async (form) => {
    await api.post("/admin/agents", { name: form.name, email: form.email, password: form.password, permissions: form.permissions });
    toast.success("Membre ajouté"); load();
  };

  const update = async (id, form) => {
    const payload = { name: form.name, permissions: form.permissions };
    if (form.password) payload.password = form.password;
    await api.put(`/admin/agents/${id}`, payload);
    toast.success("Membre mis à jour"); load();
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/agents/${id}`); toast.success("Membre supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Équipe</h1>
          <p className="mt-1.5 text-sm text-slate-500">Créez des comptes pour votre staff et définissez précisément leurs accès. Une demande prise en charge est verrouillée pour les autres.</p>
        </div>
        <StaffDialog
          title="Nouveau membre de l'équipe"
          requirePassword
          initial={{ name: "", email: "", password: "", permissions: DEFAULT_PERMS }}
          onSubmit={create}
          trigger={<Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-staff-btn"><UserPlus className="mr-1.5 h-4 w-4" /> Ajouter un membre</Button>}
        />
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="staff-list">
        {/* Super-admin card */}
        <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-5">
          <div className="flex items-start justify-between">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-blue-600 font-display text-lg font-semibold text-white">{user?.name?.charAt(0)?.toUpperCase()}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700"><ShieldCheck className="h-3 w-3" /> Super-admin</span>
          </div>
          <p className="mt-3 font-display font-semibold text-slate-900">{user?.name}</p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" /> {user?.email}</p>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-blue-700"><Lock className="h-3 w-3" /> Accès complet · gère l'équipe et les verrous.</p>
        </div>

        {team === null ? (
          <div className="py-8 text-center text-slate-400">Chargement...</div>
        ) : team.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 p-5 text-sm text-slate-400">Aucun membre. Ajoutez votre premier collaborateur.</div>
        ) : (
          team.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`staff-${a.id}`}>
              <div className="flex items-start justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-full bg-slate-100 font-display text-lg font-semibold text-slate-600">{a.name?.charAt(0)?.toUpperCase()}</span>
                <div className="flex items-center gap-1">
                  <StaffDialog
                    title={`Modifier ${a.name}`}
                    initial={{ name: a.name, password: "", permissions: a.permissions || [] }}
                    onSubmit={(form) => update(a.id, form)}
                    trigger={<button data-testid={`edit-staff-${a.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-blue-50 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>}
                  />
                  <button onClick={() => remove(a.id)} data-testid={`delete-staff-${a.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-300 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <p className="mt-3 font-display font-semibold text-slate-900">{a.name}</p>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500"><Mail className="h-3.5 w-3.5" /> {a.email}</p>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`staff-perms-${a.id}`}>
                {(a.permissions || []).length === 0 ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Aucun accès</span>
                ) : (
                  (a.permissions || []).map((p) => (
                    <span key={p} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">{PERM_LABEL[p] || p}</span>
                  ))
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
